import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import { EnhancedShiftTotals } from '../../../pages/reviewAndPublish/model';

export interface ShiftTotalsSectionStateProps {
  shiftTotals?: EnhancedShiftTotals;
}

const numberFormatter = new Intl.NumberFormat('en-US');

const formatHours = (value?: number): string =>
  value === undefined || value === null ? '--' : numberFormatter.format(value);

interface TotalRow {
  label: string;
  value?: number;
  highlighted?: boolean;
}

/**
 * Refactored (V2) Shift Totals panel — UTR-REFLEX-1163.
 *
 * Vertical stack of labelled hour rows per the mock, adding "Regular Scheduled
 * Hours" and a highlighted "Total Available Hours" row (new fields from
 * UTR-REFLEX-1172; render '--' until the backend surfaces them). Sibling of V1's
 * ShiftTotalsSection so V1 is untouched. Layout and the highlighted-row surface
 * come from Meridian (Box/Column/Row), so there's no custom CSS.
 */
const ShiftTotalsSection: React.FC<ShiftTotalsSectionStateProps> = ({ shiftTotals }) => {
  const rows: TotalRow[] = [
    { label: 'Regular Scheduled Hours', value: shiftTotals?.regularScheduledHours?.intValue },
    { label: 'VET Accepted', value: shiftTotals?.vetAcceptedHours?.intValue },
    { label: 'Ready Accepted', value: shiftTotals?.amazonReadyAcceptedHours?.intValue },
    { label: 'VTO Accepted', value: shiftTotals?.vtoAcceptedHours?.intValue },
    { label: 'Total Available Hours', value: shiftTotals?.totalAvailableHours?.intValue, highlighted: true },
  ];

  return (
    <Column spacing="400">
      <Heading type="h500" level={2}>Shift Totals</Heading>
      <Column spacing="300">
        {rows.map((row) => (
          // Every row is a card (per the mock). Regular rows use a subdued gray
          // fill; the Total Available Hours row is highlighted with an outline +
          // highlight fill, which pick up the page's blue theme.
          <Box
            key={row.label}
            type={row.highlighted ? 'outline' : 'fill'}
            backgroundColor={row.highlighted ? 'highlight' : 'subdued'}
            spacingInset="400"
          >
            <Row alignmentVertical="center" widths={['fill', 'fit']}>
              <Text type="b300">{row.label}</Text>
              <Row alignmentVertical="baseline" spacing="100">
                <Heading type="h400" level={3}>{formatHours(row.value)}</Heading>
                <Text type="b200" color="secondary">hrs</Text>
              </Row>
            </Row>
          </Box>
        ))}
      </Column>
    </Column>
  );
};

export default ShiftTotalsSection;
